import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useGetDetailIppicaRaceQuery, useGetIppicaScommesseQuery } from 'features/api/ippicaApiSlice';

import { AllCarrelloErrors } from 'lib/datoCms/types';
import { BannerHTML5, enBannerHTML5 } from 'components/bannerHTML5';
import { BannerHeroRenderer } from 'components/banner/bannerHero';
import { Category } from 'types/categoryEnum';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import { IppicaAvvenimentoPageQuery } from 'lib/datoCms/queries/getIppicaAvvenimentoPage';
import { IppicaComposer } from 'features/ippica/components/ippicaComposer';
import { IppicaDetailsRenderer } from 'features/ippica/components/ippicaDetailsRenderer';
import { IppicaInternalLayout } from 'features/ippica/components/ippicaInternalLayout';
import { NextPage } from 'next';
import { POLLING_INTERVAL_IPPICA } from 'features/ippica/constants';
import { ScommesseLayout } from 'features/scommesseLayout';
import { SkeletonIppicaArea } from 'components/skeletonIppicaArea/SkeletonIppicaArea';
import { SportTopNavigation } from 'features/sport/components/sportTopNavigation';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { TabsWithAccordionType } from 'lib/datoCms/queries/getSeoData';
import { errorLabelsUpdated } from 'features/carrello/carrelloSlice';
import { selectCodiceStatoAvvenimento } from 'features/ippica/selectors';
import { useCodProgrammaIdAvvenimentoByRouter } from 'features/ippica/components/hook';
import { useEffect, useMemo } from 'react';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useIsNavigating } from 'hooks/useIsNavigating';
import useWindowSize from 'hooks/useWindowSize';
import { isEqualStatoScommessaByKey } from 'features/ippica/functions';
import { Stato } from 'features/ippica/types';

type Props = {
  seoData?: TabsWithAccordionType;
  allCarrelloErrors: AllCarrelloErrors[];
  ippicaAvvenimentoPage: IppicaAvvenimentoPageQuery['ippicaAvvenimentoPage'];
};

const PageContent: NextPage<Props> = ({ ippicaAvvenimentoPage, allCarrelloErrors, seoData }: Props) => {
  const labelCerca = useGetLabelByKey('search');
  const labelSport = useGetLabelByKey('sport');
  const labelIppica = useGetLabelByKey('ippica');
  const codiceStatoAvvenimento = useTypedSelector(selectCodiceStatoAvvenimento);

  const dispatch = useAppDispatch();
  const { isMobile } = useWindowSize();

  const isClosedRace = useMemo(
    () => isEqualStatoScommessaByKey(codiceStatoAvvenimento, Stato.InPagamento),
    [codiceStatoAvvenimento]
  );

  useEffect(() => {
    dispatch(errorLabelsUpdated(allCarrelloErrors));
  }, [allCarrelloErrors, dispatch]);

  const isNavigating = useIsNavigating();

  const { idProgramma, idAvvenimento } = useCodProgrammaIdAvvenimentoByRouter();

  const { isLoading, data } = useGetDetailIppicaRaceQuery(
    { idProgramma, idAvvenimento },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: POLLING_INTERVAL_IPPICA,
      skip: isClosedRace,
    }
  );

  useGetIppicaScommesseQuery(
    { idProgramma, idAvvenimento },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: POLLING_INTERVAL_IPPICA,
      skip: isLoading || isClosedRace,
    }
  );

  //const isIppicaDataLoading = useTypedSelector(selectIsLoadingIppicaDetailsRace);

  const { visualComposer, id, heroBanner } = ippicaAvvenimentoPage;
  return (
    <ScommesseLayout hasNoPaddingOnMain>
      <HeaderTitleWithSearch
        title={labelSport!}
        titleHighlighted={labelIppica!}
        category={Category.Sport}
        labelSearch={labelCerca!}
      />
      <BannerHeroRenderer pageId={id} heroBanner={heroBanner?.[0]} />
      <SportTopNavigation hasFirstElementFixed />
      <BannerHTML5 url={enBannerHTML5.Ippica} />
      <IppicaInternalLayout>
        {isNavigating || isLoading || !data ? <SkeletonIppicaArea /> : <IppicaDetailsRenderer />}
        {visualComposer && <IppicaComposer pageId={id} cmsData={visualComposer} />}
        <TabsWithAccordion tabs={seoData?.tabs ?? []} />
      </IppicaInternalLayout>
    </ScommesseLayout>
  );
};

export default PageContent;
